import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InputBar from './InputBar';
import ReplyBox from './ReplyBox';
import SendFiles from './SendFiles';
import { sendToastMessage } from '../../../../actions/toast';
import { chatType, maxChatFileSizeInBytes } from '../../ChatController';
import { debounce, GetQueryParam } from '../../../../utils/common';
import { getFileType } from '../../../../utils/common';
import { getUsersForMentions, sendChatMessages } from '../../../../actions/chat';

export const transformMentionsForSubmit = (mentions, users) => {
    const tags = mentions.map((mention) => {
        const position = mention.plainTextIndex;
        const allUser = { userId: -2, username: 'All', role: '' };
        if (mention.id === '@all') {
            return { position, user: allUser };
        }
        const mentionedUser = users?.find((user) => user._id === mention.id);
        return { position, user: mentionedUser };
    });

    return tags;
};

export const getHeightOfUploaded = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        let height, width;

        reader.onload = (e) => {
            const imageUrl = e.target.result;
            const img = new Image();

            img.src = imageUrl;

            img.onload = () => {
                width = img.width;
                height = img.height;
                resolve({ height, width });
            };

            img.onerror = () => {
                reject('Failed to load image.');
            };
        };

        reader.readAsDataURL(file);
    });
};

const isHeicFile = (file) => {
    const heicExtensions = ['.heic', '.heif'];
    const heicMimeTypes = ['image/heic', 'image/heif'];
    const fileName = file.name.toLowerCase();
    const hasHeicExtension = heicExtensions.some(ext => fileName.endsWith(ext));
    const hasHeicMimeType = heicMimeTypes.includes(file.type.toLowerCase());
    // Also check if type is empty string and file has HEIC extension
    const isEmptyTypeWithHeicExt = !file.type && hasHeicExtension;

    return hasHeicMimeType || hasHeicExtension || isEmptyTypeWithHeicExt;
};

const convertHeicToJpg = async (file) => {
    try {
        const convertedBlob = await import('heic2any').then(m => m.default({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9
        }));

        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        return new File([blob], fileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
        });
    } catch (error) {
        console.error('Error converting HEIC to JPG:', error);
        throw error;
    }
};

const MessageInput = forwardRef(({ replyShow, setReplyShow, closeReply, replyToMessage, showAllTag, showDoubleHash = false, ticketId, isSupportTicket, isChatPopup, uuid, chat_id, type }, ref) => {

    const dispatch = useDispatch();
    const [inputTextValue, setInputTextValue] = useState('');
    const { conversationDetails } = useSelector((state) => state.conversations);
    const chatDetails = useSelector((state) => state.chat.chatDetails);
    let openedConversation = chatDetails?.[chat_id];
    if (ticketId) openedConversation = conversationDetails?.[ticketId];
    const [showFileUi, setshowFileUi] = useState(false);
    const [files, setFile] = useState([]);
    const [isConvertingHeic, setIsConvertingHeic] = useState(false);
    const isScogoGptChat = GetQueryParam("chatGpt", Boolean);
    const usersForMentions = useSelector((state) => state.chat.usersForMentions);
    let inputBarRef = useRef();

    const clearInput = useCallback(() => {
        setInputTextValue('');
        closeReply();
        setReplyShow(false);
    }, [closeReply, setReplyShow]);

    const onFileSelect = useCallback(async (event) => {
        let uploadedData = [...event.target.files];
        let newFiles = [...files];
        let maxSizeFileNamesList = [];

        const hasHeic = uploadedData.some(isHeicFile);
        if (hasHeic) setIsConvertingHeic(true);

        try {
            // Convert all HEIC files IN PARALLEL
            const convertedData = await Promise.all(
                uploadedData.map(async (file) => {
                    if (isHeicFile(file)) {
                        try {
                            return await convertHeicToJpg(file);
                        } catch (error) {
                            console.error('Failed to convert HEIC file:', error);
                            dispatch(sendToastMessage({ status: 'danger', message: `Failed to convert ${file.name}. Please try a different format.` }));
                            return null; // null = skip this file
                        }
                    }
                    return file;
                })
            );

            // Process size-checks and dimension reads in parallel too
            const fileEntries = await Promise.all(
                convertedData.map(async (file, idx) => {
                    if (!file) return null; // conversion failed
                    if (file.size > maxChatFileSizeInBytes) {
                        maxSizeFileNamesList.push(file.name);
                        return null;
                    }
                    const id = Date.now() + idx;
                    try {
                        let f = { id, caption: '', file };
                        if (getFileType(file.type) === 'image') {
                            const { height, width } = await getHeightOfUploaded(file);
                            f = { ...f, height, width };
                        }
                        return f;
                    } catch (error) {
                        console.error(error);
                        return null;
                    }
                })
            );

            newFiles = [...newFiles, ...fileEntries.filter(Boolean)];

            if (maxSizeFileNamesList.length) {
                const message = `File Size of ${maxSizeFileNamesList.join(', ')} ${maxSizeFileNamesList.length > 1 ? 'are' : 'is'} bigger than ${maxChatFileSizeInBytes / 1000000} MB`;
                dispatch(sendToastMessage({ status: 'danger', message }));
            }

            if (newFiles.length > 20) {
                dispatch(sendToastMessage({ status: 'danger', message: 'Cannot send more than 20 files at a time' }));
            }

            if (newFiles.length > 0) {
                setFile(newFiles.slice(0, 20));
                setshowFileUi(true);
            }
        } finally {
            setIsConvertingHeic(false);
        }
    }, [dispatch, files]);


    useImperativeHandle(ref, () => ({
        onFileSelect: (files) => {
            onFileSelect({ target: { files } });
        },
    }));

    const handlePaste = useCallback(
        async (evt) => {
            const clipboardItems = evt.clipboardData.items;
            const items = [].slice.call(clipboardItems).filter(function (item) {
                // Filter the image items only
                return /^image\//.test(item.type);
            });
            if (items.length === 0) return;
            let blob = items[0].getAsFile();
            let fileName = `image${files.length + 1}.jpg`;

            // Check if it's a HEIC file
            if (isHeicFile(blob)) {
                setIsConvertingHeic(true);
                try {
                    blob = await convertHeicToJpg(blob);
                    fileName = blob.name; // Use the converted file name
                } catch (error) {
                    console.error('Failed to convert pasted HEIC image:', error);
                    dispatch(sendToastMessage({ status: 'danger', message: 'Failed to convert pasted image. Please try a different format.' }));
                    return;
                } finally {
                    setIsConvertingHeic(false);
                }
            }

            let file = new File([blob], fileName, { type: blob.type || 'image/jpeg', lastModified: new Date().getTime() }, 'utf-8');
            onFileSelect({ target: { files: [file] } });
        },
        [onFileSelect, files.length, dispatch]
    );

    useEffect(() => {
        clearInput();
    }, [chat_id, clearInput]);

    const taggableUsers = useMemo(() => {
        const allUser = { display: 'All', id: '@all' };
        const chat = chatDetails[chat_id]
        const members = chat?.members || [];
        const transformed = members?.map((user) => ({ id: user.id, display: user.display_name, ...user })) || [];
        let list = [];
        if (transformed.length > 0) {
            list = [...transformed];
            if ([chatType.GROUP, chatType.TICKET].includes(chat?.type)) {
                list = [...transformed];
            }
        }
        if (type === chatType.HELP) {
            list = [...list, ...usersForMentions];
        }
        return list;
    }, [chatDetails, chat_id, usersForMentions, type]);

    const onFileSend = async (files) => {
        let payload = {
            content: '',
            medias: files,
            type: "DEFAULT",
            mentions: [],
            metadata: {}
        };
        if (replyShow && replyToMessage) {
            payload.replied_to = replyToMessage.id;
        }
        dispatch(sendChatMessages({ id: chat_id, payload }))
        closeFileUI();
        setReplyShow(false);
    };

    const closeFileUI = useCallback(() => {
        setshowFileUi(false);
        setFile([]);
    }, []);




    const getHelpUsers = ({ query }) => {
        if (query && query.length > 0 && query.startsWith('@')) {
            query = query.slice(1);
        }
        dispatch(getUsersForMentions({ query }));
    }

    const debouncedGetHelpUsers = useCallback(debounce(getHelpUsers, 1000), []);

    const handleInputChange = (inputValue, mentions) => {
        if (type === chatType.HELP) {
            debouncedGetHelpUsers({ query: inputValue });
            return;
        }
        setInputTextValue(inputValue);
    };

    useEffect(() => {
        closeFileUI()
    }, [closeFileUI, chat_id])

    return (
        <div className='chatInputBar'>
            {isConvertingHeic && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                    <div className='bg-gray-50 rounded-lg p-6 flex flex-col items-center shadow-xl'>
                        <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-scogoprimary mb-4'></div>
                        <p className='text-font12 text-gray-700'>Loading...</p>
                        <p className='text-font10 text-gray-500 mt-2'>Please wait</p>
                    </div>
                </div>
            )}
            {replyShow && <ReplyBox closeReply={closeReply} message={replyToMessage} timestamp={replyToMessage.timestamp} isChatPopup={isChatPopup} setReplyShow={setReplyShow} />}
            {showFileUi && (
                <SendFiles
                    chat_id={chat_id}
                    users={openedConversation?.users}
                    taggableUsers={taggableUsers}
                    closeFileUI={closeFileUI}
                    onFileSelect={onFileSelect}
                    onFileSend={onFileSend}
                    files={files}
                    setFile={setFile}
                    isChatPopup={isChatPopup}
                    replyShow={replyShow}
                />
            )}
            {!showFileUi && <InputBar
                allowFileUpload={!isScogoGptChat}
                setInputTextValue={setInputTextValue}
                inputTextValue={inputTextValue}
                handleInputChange={handleInputChange}
                onFileSelect={onFileSelect}
                showAllTag={showAllTag}
                taggableUsers={taggableUsers}
                conversationId={chat_id}
                replyShow={replyShow}
                handlePaste={handlePaste}
                showDoubleHash={showDoubleHash}
                chat_id={chat_id}
                uuid={uuid}
                replyToMessage={replyToMessage}
                setReplyShow={setReplyShow}
                inputBarRef={inputBarRef}
            />}

        </div>
    );
});

export default MessageInput;
